/*
 * Collection sidebar filters: sticky positioning + smooth open/close.
 *
 * This is scoped to collection pages so the global custom.js file does not
 * carry collection-only behavior across the whole storefront.
 */

(function () {
  if (window.CatoriCollectionStickySidebarLoaded) return;
  window.CatoriCollectionStickySidebarLoaded = true;

  const SIDEBAR_SELECTORS = {
    container: '[data-section-type="collection"]',
    sidebar: '[data-collection-sidebar]',
    slideOut: '[data-collection-sidebar-slide-out]',
    scrollable: '.collection__sidebar',
    products: '.collection__products',
    productsGrid: '[data-products-grid]',
    toggleButton: '[data-aria-toggle]',
  };

  const MIN_STICKY_SCROLL_SPACE = 80;

  function isMobileView() {
    const small = (window.theme && window.theme.sizes && window.theme.sizes.small) || 750;
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    return width < small;
  }

  function getWindowHeight() {
    return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  }

  function CollectionSidebarSticky(container) {
    this.container = container;
    this.sidebar = container.querySelector(SIDEBAR_SELECTORS.sidebar);
    this.toggleButton = container.querySelector(SIDEBAR_SELECTORS.toggleButton);

    if (!this.sidebar) return;

    this.placeholder = null;
    this.positionFrame = null;
    this.inlineOpening = false;
    this.inlineOpeningTimeout = null;
    this.openingPlaceholderPrepared = false;

    this.onWheel = this.onWheel.bind(this);
    this.requestPositionUpdate = this.requestPositionUpdate.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    this.sidebar.addEventListener('wheel', this.onWheel, {passive: false});
    this.sidebar.addEventListener('transitionend', this.onTransitionEnd);
    window.addEventListener('scroll', this.requestPositionUpdate, {passive: true});
    document.addEventListener('theme:resize', this.requestPositionUpdate);

    this.positionObserver = new MutationObserver(this.requestPositionUpdate);
    this.positionObserver.observe(this.sidebar, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style'],
    });

    if (this.toggleButton) {
      this.toggleObserver = new MutationObserver(
        function (mutationList) {
          for (let i = 0; i < mutationList.length; i++) {
            const mutation = mutationList[i];
            if (mutation.type !== 'attributes') continue;

            const expanded = mutation.target.getAttribute('aria-expanded') === 'true';
            const wasExpanded = mutation.oldValue === 'true';

            if (expanded) {
              if (!wasExpanded) this.startInlineOpening();
            } else {
              this.cancelInlineOpening();
              if (wasExpanded) this.startInlineClosing();
            }
          }
        }.bind(this)
      );
      this.toggleObserver.observe(this.toggleButton, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['aria-expanded'],
      });
    }

    this.requestPositionUpdate();
  }

  CollectionSidebarSticky.prototype = {
    isDesktopInline: function () {
      return !isMobileView() && this.container.querySelector(SIDEBAR_SELECTORS.slideOut) === null;
    },

    isPlaceholderVisible: function () {
      return this.placeholder && this.placeholder.style.display !== 'none';
    },

    isFilterExpanded: function () {
      if (this.toggleButton) {
        return this.toggleButton.getAttribute('aria-expanded') === 'true';
      }

      return this.sidebar.classList.contains('expanded');
    },

    onTransitionEnd: function (event) {
      if (event.target === this.sidebar && (event.propertyName === 'width' || event.propertyName === 'min-width')) {
        this.finishInlineOpening();
      }
    },

    startInlineOpening: function () {
      if (!this.isDesktopInline()) return;

      this.inlineOpening = true;
      this.openingPlaceholderPrepared = false;
      this.prepareOpeningPlaceholder();
      this.requestPositionUpdate();

      if (this.inlineOpeningTimeout) window.clearTimeout(this.inlineOpeningTimeout);
      this.inlineOpeningTimeout = window.setTimeout(this.finishInlineOpening.bind(this), 700);
    },

    finishInlineOpening: function () {
      if (!this.inlineOpening) return;

      this.inlineOpening = false;
      this.openingPlaceholderPrepared = false;

      if (this.inlineOpeningTimeout) {
        window.clearTimeout(this.inlineOpeningTimeout);
        this.inlineOpeningTimeout = null;
      }

      this.clearPlaceholderTransition();
      this.requestPositionUpdate();
    },

    cancelInlineOpening: function () {
      this.inlineOpening = false;
      this.openingPlaceholderPrepared = false;

      if (this.inlineOpeningTimeout) {
        window.clearTimeout(this.inlineOpeningTimeout);
        this.inlineOpeningTimeout = null;
      }

      this.clearPlaceholderTransition();
    },

    startInlineClosing: function () {
      this.clearPlaceholderTransition();
      this.resetPosition();
      this.resetScrollStyles();
    },

    ensurePlaceholder: function () {
      if (this.placeholder) return;

      this.placeholder = document.createElement('div');
      this.placeholder.className = 'collection__sidebar__placeholder';
      this.placeholder.setAttribute('aria-hidden', 'true');
      this.placeholder.style.display = 'none';
      this.sidebar.parentNode.insertBefore(this.placeholder, this.sidebar);
    },

    prepareOpeningPlaceholder: function () {
      if (this.openingPlaceholderPrepared) return;

      this.ensurePlaceholder();

      this.placeholder.style.transitionProperty = 'none';
      this.placeholder.style.transitionDuration = '0ms';
      this.placeholder.style.flex = '0 0 0px';
      this.placeholder.style.width = '0px';
      this.placeholder.style.minWidth = '0px';
      this.placeholder.style.marginRight = '-2px';
      this.placeholder.style.display = 'block';

      void this.placeholder.offsetWidth;

      this.placeholder.style.transitionProperty = 'width, min-width, flex-basis, margin-right';
      this.placeholder.style.transitionDuration = 'var(--duration)';
      this.placeholder.style.transitionTimingFunction = 'cubic-bezier(0.215, 0.61, 0.355, 1)';
      this.openingPlaceholderPrepared = true;
    },

    clearPlaceholderTransition: function () {
      if (!this.placeholder) return;

      this.placeholder.style.removeProperty('transition-property');
      this.placeholder.style.removeProperty('transition-duration');
      this.placeholder.style.removeProperty('transition-timing-function');
    },

    resetScrollStyles: function () {
      const scrollable = this.sidebar.querySelector(SIDEBAR_SELECTORS.scrollable);
      if (!scrollable) return;

      scrollable.style.removeProperty('max-height');
      scrollable.style.removeProperty('overflow-y');
    },

    getExpandedWidth: function (fallbackWidth) {
      const scrollable = this.sidebar.querySelector(SIDEBAR_SELECTORS.scrollable);
      const scrollableStyles = scrollable ? getComputedStyle(scrollable) : null;
      const width = scrollable ? scrollable.offsetWidth : 0;
      const minWidth = scrollableStyles ? parseFloat(scrollableStyles.minWidth) : 0;
      const rootWidth = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width'));

      return width || minWidth || rootWidth || fallbackWidth || this.sidebar.offsetWidth;
    },

    getProductsContentHeight: function (products) {
      const productsGrid = products.querySelector(SIDEBAR_SELECTORS.productsGrid);
      if (!productsGrid) return products.offsetHeight;

      return Math.max(productsGrid.offsetHeight, productsGrid.scrollHeight, productsGrid.getBoundingClientRect().height);
    },

    getFixedTop: function () {
      const collectionNav = this.container.querySelector('[data-collection-nav]');
      const collectionNavHeight = collectionNav ? collectionNav.offsetHeight : 0;
      const hasStickyHeader = document.querySelector('[data-header-sticky]');
      const headerElement = document.querySelector('[data-header-height]');
      const headerHeight = hasStickyHeader && headerElement ? headerElement.offsetHeight : 0;
      const borderWidth = hasStickyHeader && document.body.classList.contains('has-line-design') ? 1 : 0;

      return Math.max(0, headerHeight + collectionNavHeight - borderWidth);
    },

    resetPosition: function (hidePlaceholder) {
      if (hidePlaceholder === undefined) hidePlaceholder = true;

      ['position', 'top', 'left', 'width', 'min-width', 'height', 'z-index', 'transform', 'align-self'].forEach(
        function (property) {
          if (this.sidebar.style.getPropertyValue(property)) {
            this.sidebar.style.removeProperty(property);
          }
        }.bind(this)
      );

      if (hidePlaceholder && this.placeholder) {
        this.placeholder.style.display = 'none';
      }
    },

    requestPositionUpdate: function () {
      if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame);

      this.positionFrame = window.requestAnimationFrame(
        function () {
          this.positionFrame = null;
          this.updatePosition();
        }.bind(this)
      );
    },

    updatePosition: function () {
      const products = this.sidebar.closest(SIDEBAR_SELECTORS.products);
      const scrollable = this.sidebar.querySelector(SIDEBAR_SELECTORS.scrollable);
      const setImportant = function (element, property, value) {
        if (element.style.getPropertyValue(property) !== value || element.style.getPropertyPriority(property) !== 'important') {
          element.style.setProperty(property, value, 'important');
        }
      };
      const removeProperty = function (element, property) {
        if (element.style.getPropertyValue(property)) {
          element.style.removeProperty(property);
        }
      };

      if (!this.isDesktopInline() || !products || !this.isFilterExpanded()) {
        this.resetPosition();

        if (scrollable) {
          removeProperty(scrollable, 'max-height');
          removeProperty(scrollable, 'overflow-y');
        }

        return;
      }

      const isOpening = this.inlineOpening || (this.sidebar.classList.contains('expanding') && this.sidebar.classList.contains('expanded'));

      if (isOpening) {
        this.prepareOpeningPlaceholder();
      }

      this.ensurePlaceholder();

      const sidebarSourceRect =
        this.placeholder && this.placeholder.offsetParent !== null ? this.placeholder.getBoundingClientRect() : this.sidebar.getBoundingClientRect();
      const productsRect = products.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const fixedTop = this.getFixedTop();
      const sidebarWidth = isOpening ? this.getExpandedWidth(sidebarSourceRect.width) : sidebarSourceRect.width || this.sidebar.offsetWidth;
      const sidebarLeft = sidebarSourceRect.left;
      const productsTop = productsRect.top + scrollY;
      const sidebarHeight = Math.max(0, getWindowHeight() - fixedTop);
      const productsContentHeight = this.getProductsContentHeight(products);
      const productsHeight = Math.max(products.offsetHeight, productsContentHeight);
      const productsBottom = productsTop + productsHeight;
      const fixedBottom = scrollY + fixedTop + sidebarHeight;

      if (productsContentHeight <= sidebarHeight + MIN_STICKY_SCROLL_SPACE) {
        this.resetPosition(true);

        if (scrollable) {
          removeProperty(scrollable, 'max-height');
          removeProperty(scrollable, 'overflow-y');
        }

        return;
      }

      this.placeholder.style.flex = '0 0 ' + sidebarWidth + 'px';
      this.placeholder.style.width = sidebarWidth + 'px';
      this.placeholder.style.minWidth = sidebarWidth + 'px';
      this.placeholder.style.marginRight = getComputedStyle(this.sidebar).marginRight;

      if (productsHeight <= sidebarHeight || scrollY + fixedTop <= productsTop) {
        this.resetPosition(true);
      } else if (fixedBottom >= productsBottom) {
        const absoluteTop = Math.max(0, productsHeight - sidebarHeight);

        this.placeholder.style.display = 'block';
        setImportant(this.sidebar, 'position', 'absolute');
        setImportant(this.sidebar, 'top', absoluteTop + 'px');
        setImportant(this.sidebar, 'left', sidebarSourceRect.left - productsRect.left + 'px');
        setImportant(this.sidebar, 'width', sidebarWidth + 'px');
        setImportant(this.sidebar, 'height', sidebarHeight + 'px');
        setImportant(this.sidebar, 'z-index', '10');
        setImportant(this.sidebar, 'transform', 'none');
        setImportant(this.sidebar, 'align-self', 'flex-start');
      } else {
        this.placeholder.style.display = 'block';
        setImportant(this.sidebar, 'position', 'fixed');
        setImportant(this.sidebar, 'top', fixedTop + 'px');
        setImportant(this.sidebar, 'left', sidebarLeft + 'px');
        setImportant(this.sidebar, 'width', sidebarWidth + 'px');
        setImportant(this.sidebar, 'height', sidebarHeight + 'px');
        setImportant(this.sidebar, 'z-index', '10');
        setImportant(this.sidebar, 'transform', 'none');
        setImportant(this.sidebar, 'align-self', 'flex-start');
      }

      if (scrollable) {
        setImportant(scrollable, 'max-height', '100%');
        setImportant(scrollable, 'overflow-y', 'auto');
      }
    },

    onWheel: function (evt) {
      if (!this.isDesktopInline()) return;

      const scrollable = this.sidebar.querySelector(SIDEBAR_SELECTORS.scrollable);
      if (!scrollable || scrollable.scrollHeight <= scrollable.clientHeight || evt.deltaY === 0) return;

      const atTop = scrollable.scrollTop <= 0;
      const atBottom = Math.ceil(scrollable.scrollTop + scrollable.clientHeight) >= scrollable.scrollHeight;
      const shouldScrollPage = (evt.deltaY < 0 && atTop) || (evt.deltaY > 0 && atBottom);

      if (shouldScrollPage) {
        evt.preventDefault();
        window.scrollBy(0, evt.deltaY);
      }
    },

    destroy: function () {
      this.cancelInlineOpening();
      this.sidebar.removeEventListener('wheel', this.onWheel);
      this.sidebar.removeEventListener('transitionend', this.onTransitionEnd);
      window.removeEventListener('scroll', this.requestPositionUpdate);
      document.removeEventListener('theme:resize', this.requestPositionUpdate);

      if (this.positionObserver) this.positionObserver.disconnect();
      if (this.toggleObserver) this.toggleObserver.disconnect();
      if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame);

      this.resetPosition();

      if (this.placeholder && this.placeholder.parentNode) {
        this.placeholder.parentNode.removeChild(this.placeholder);
        this.placeholder = null;
      }
    },
  };

  let sidebarInstances = [];

  function initCollectionSidebars(root) {
    (root || document).querySelectorAll(SIDEBAR_SELECTORS.container).forEach(function (container) {
      const alreadyInitialized = sidebarInstances.some(function (instance) {
        return instance.container === container;
      });

      if (!alreadyInitialized) {
        const instance = new CollectionSidebarSticky(container);
        if (instance.sidebar) sidebarInstances.push(instance);
      }
    });
  }

  function destroyCollectionSidebars(target) {
    sidebarInstances = sidebarInstances.filter(function (instance) {
      if (!target || target.contains(instance.container) || target === instance.container) {
        instance.destroy();
        return false;
      }
      return true;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCollectionSidebars();
    });
  } else {
    initCollectionSidebars();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initCollectionSidebars(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyCollectionSidebars(event.target);
  });
})();
